import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Checkbox } from 'antd';
import { useMatch } from 'react-router-dom';

import { Compensation } from 'types';
import { Attachment, CompensationStatusItem } from 'components/common';
import { Language, Namespace, Path } from 'enums';
import { getAllEmployees, getLanguage, getRules } from 'store';
import { fetchEmployees, fetchRules } from 'services';
import { DATE_FORMAT, DATE_PERIOD_FORMAT } from 'constants-module';
import { useAppSelector, useFetchSelector } from 'hooks';

import {
  CONSENT_REQUIRED_TYPES,
  getConsentTextKeys,
} from '../../common/components/CompensationForm/consent-policy.config';
import { CompensationViewField, CompensationViewRow } from './CompensationViewField';
import styles from './CompensationView.module.scss';

interface Props {
  compensation: Compensation;
  isAttachmentsExpired: boolean;
}

export const CompensationView = ({ compensation, isAttachmentsExpired }: Props) => {
  const language = useAppSelector(getLanguage);
  const rules = useFetchSelector(getRules, fetchRules);
  const employees = useFetchSelector(getAllEmployees, fetchEmployees);
  const { t } = useTranslation(Namespace.CompensationForm);
  const { t: tCompensationTypes } = useTranslation(Namespace.CompensationTypes);

  const isEmployeeApplicationPage = useMatch(`${Path.Requests}/*`);

  const employee = useMemo(
    () => employees.find(({ employeeID }) => compensation.employeeID === employeeID),
    [employees, compensation],
  );

  const rule = useMemo(
    () => rules.find(({ compensationType }) => compensation.type === compensationType),
    [rules, compensation],
  );

  return (
    <div className={styles.compensationViewContainer}>
      <CompensationViewRow className={styles.employeeRow}>
        <CompensationViewField>
          <div className="compensation-view__title">{t('employee')}</div>
          <div className="compensation-view__value">
            {employee && language === Language.RU
              ? `${employee?.lastName} ${employee?.firstName} `
              : `${employee?.lastNameEn} ${employee?.firstNameEn} `}
            ({employee?.login})
          </div>
        </CompensationViewField>
        {isEmployeeApplicationPage && (
          <CompensationViewField>
            <div className="compensation-view__title">{t('employeeStatus')}</div>
            <div className="compensation-view__value">
              {t(compensation.employmentStatus, { ns: Namespace.EmployeeStatuses })}
            </div>
          </CompensationViewField>
        )}
      </CompensationViewRow>
      <CompensationViewField>
        <div className="compensation-view__title">{t('legalEntity')}</div>
        <div className="compensation-view__value">{compensation.legalEntity}</div>
      </CompensationViewField>
      <CompensationViewField>
        <div className="compensation-view__title">{t('compensationType')}</div>
        <div className="compensation-view__value">{tCompensationTypes(compensation.type)}</div>
      </CompensationViewField>
      <CompensationViewRow>
        <CompensationViewField>
          <div className="compensation-view__title">
            {!rule || rule.requiresBill ? t('billAmount') : t('paymentAmount')}
          </div>
          <div className="compensation-view__value">{compensation.amount}</div>
        </CompensationViewField>
        <CompensationViewField>
          <div className="compensation-view__title">{t('currency')}</div>
          <div className="compensation-view__value">{compensation.currency}</div>
        </CompensationViewField>
        {compensation.billedOn && (
          <CompensationViewField>
            <div className="compensation-view__title">{t('billedOn')}</div>
            <div className="compensation-view__value">{dayjs(compensation.billedOn).format(DATE_FORMAT)}</div>
          </CompensationViewField>
        )}
      </CompensationViewRow>
      <CompensationViewField>
        <div className="compensation-view__title">{t('targetPeriod')}</div>
        <div className="compensation-view__value">{dayjs(compensation.targetPeriod).format(DATE_PERIOD_FORMAT)}</div>
      </CompensationViewField>
      {compensation.employeeComment && (
        <CompensationViewField>
          <div className="compensation-view__title">{t('employeeComment')}</div>
          <div className="compensation-view__value">{compensation.employeeComment}</div>
        </CompensationViewField>
      )}
      {compensation.reviewerComment && (
        <CompensationViewField>
          <div className="compensation-view__title">{t('reviewerComment')}</div>
          <div className="compensation-view__value">{compensation.reviewerComment}</div>
        </CompensationViewField>
      )}
      <CompensationViewField>
        <div className="compensation-view__title">{t('status')}</div>
        <div className="compensation-view__value">
          <CompensationStatusItem status={compensation.status} />
        </div>
      </CompensationViewField>
      {CONSENT_REQUIRED_TYPES.includes(compensation.type) && compensation.privacyConsent && (
        <div className={styles.personalDataProcessingConsent}>
          <Checkbox checked={compensation.privacyConsent} disabled />
          <div className="consent">{t(getConsentTextKeys(compensation.type).label)}</div>
        </div>
      )}
      {!!compensation.attachments.length && (
        <CompensationViewField>
          <div className="compensation-view__title">{t('attachments')}</div>
          <CompensationViewRow className="compensation-view__value">
            {isAttachmentsExpired ? (
              <span className={styles.deletedAttachmentMessage}>{t('expiredAttachments')}</span>
            ) : (
              compensation.attachments.map(({ attachmentID, originalName }) => (
                <Attachment
                  key={attachmentID}
                  fileName={originalName}
                  attachmentId={attachmentID}
                  requestId={compensation.compensationRequestID}
                />
              ))
            )}
          </CompensationViewRow>
        </CompensationViewField>
      )}
    </div>
  );
};
